import React from 'react';
import PropTypes from 'prop-types';

import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';

import { ReactComponent as InitioLogo } from '../../images/initio-logo.svg';
import redwoodseedLogo from '../../images/redwoodseed-logo.png';
import { getRedwoodSeedUrl, getRedwoodSeedHostLabel } from '../../utils/redwoodSeedUrl';

import './InitioMovedAnnouncement.scss';

const REDWOOD_SEED_URL = getRedwoodSeedUrl();
const REDWOOD_SEED_HOST = getRedwoodSeedHostLabel();
const SUPPORT_EMAIL = 'alexander@redwoodvaluation.com';

export default function InitioMovedAnnouncement({
  primaryHref = REDWOOD_SEED_URL,
  primaryLabel = 'Go to Redwood Seed',
  secondaryLabel = 'Back to login',
  onSecondary,
}) {
  return (
    <section className="InitioMovedAnnouncement" aria-label="Platform transition notice">
      <div className="InitioMovedAnnouncement__decor InitioMovedAnnouncement__decor--tl" aria-hidden="true" />
      <div className="InitioMovedAnnouncement__decor InitioMovedAnnouncement__decor--br" aria-hidden="true" />

      <div className="InitioMovedAnnouncement__logos">
        <InitioLogo className="InitioMovedAnnouncement__initio-logo" />
        <ArrowForwardRoundedIcon className="InitioMovedAnnouncement__arrow" />
        <img
          className="InitioMovedAnnouncement__redwood-logo"
          src={redwoodseedLogo}
          alt="Redwood Seed by Redwood Valuation"
        />
      </div>

      <h1 className="InitioMovedAnnouncement__title">We&rsquo;ve Moved to a New Platform</h1>

      <p className="InitioMovedAnnouncement__intro">
        We&rsquo;re writing to let you know that Initio Software has transitioned our services
        to our new platform, Redwood Seed by Redwood Valuation.
      </p>

      <p className="InitioMovedAnnouncement__means">What this means for you:</p>

      <div className="InitioMovedAnnouncement__cards">
        <article className="InitioMovedAnnouncement__card InitioMovedAnnouncement__card--past">
          <div className="InitioMovedAnnouncement__card-heading">
            <h2>Past valuations</h2>
            <div className="InitioMovedAnnouncement__icon" aria-hidden="true">
              <DescriptionOutlinedIcon />
            </div>
          </div>
          <p>
            You can still view your existing valuations here on Initio Software —
            no action needed for historical records.
          </p>
        </article>

        <article className="InitioMovedAnnouncement__card InitioMovedAnnouncement__card--future">
          <div className="InitioMovedAnnouncement__card-heading">
            <h2>Future valuations</h2>
            <div className="InitioMovedAnnouncement__icon" aria-hidden="true">
              <OpenInNewRoundedIcon />
            </div>
          </div>
          <p>
            Starting now, all new valuation requests will be handled through Redwood Seed.
            To get started, please create an account at
            {' '}
            <a
              className="InitioMovedAnnouncement__ext-link"
              href={REDWOOD_SEED_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {REDWOOD_SEED_HOST}
              <OpenInNewRoundedIcon />
            </a>
            {' '}
            and submit your materials there.
          </p>
        </article>
      </div>

      <div className="InitioMovedAnnouncement__support">
        <PersonOutlineRoundedIcon aria-hidden="true" />
        <p>
          If you have any questions about the transition or need help getting set up on the
          new platform, don&rsquo;t hesitate to reach out to
          {' '}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
          .
        </p>
      </div>

      <div className="InitioMovedAnnouncement__actions">
        {secondaryLabel && onSecondary && (
          <button
            type="button"
            className="InitioMovedAnnouncement__back"
            onClick={onSecondary}
          >
            {secondaryLabel}
          </button>
        )}
        <button
          type="button"
          className="InitioMovedAnnouncement__cta"
          onClick={() => window.location.assign(primaryHref)}
        >
          {primaryLabel}
          <ArrowForwardRoundedIcon />
        </button>
      </div>
    </section>
  );
}

InitioMovedAnnouncement.propTypes = {
  primaryHref: PropTypes.string,
  primaryLabel: PropTypes.string,
  secondaryLabel: PropTypes.string,
  onSecondary: PropTypes.func,
};

InitioMovedAnnouncement.defaultProps = {
  primaryHref: REDWOOD_SEED_URL,
  primaryLabel: 'Go to Redwood Seed',
  secondaryLabel: 'Back to login',
  onSecondary: undefined,
};
